import {useDispatch, useSelector} from 'react-redux';

import deviceSettingTypes from '../../../consts/device/deviceSettingTypes';
import {ENABLE_ERRORS_DISPLAY} from '../../../consts/tnc/aggregatedSettingTypes';
import appErrorService from '../../../services/app/appErrorService';
import appNotificationsService from '../../../services/app/appNotificationsService';
import iotDeviceConfig from '../../../services/iotDevice/iotDeviceConfig';
import iotDeviceFeatureConfigService from '../../../services/iotDevice/iotDeviceFeatureConfigService';
import iotDeviceSettingsService from '../../../services/iotDevice/iotDeviceSettingsService';
import marketConfigService from '../../../services/marketConfigService';
import parentalProtectionService from '../../../services/parentalProtectionService';
import productService from '../../../services/product/productService';
import appRouterService from '../../../services/route/appRouterService';
import tncService from '../../../services/tncService';
import {showDeviceLockedModalAction} from '../../../state/ducks/modal';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../../state/selectors/consumer';
import {selectIsScpCheckInProgress} from '../../../state/selectors/global';
import {selectIsDeviceReady, selectIsMonoDevice} from '../../../state/selectors/iotDevice';
import useIsGetFeatureAvailable from './useIsGetFeatureAvailable';

const deviceReadySettingTypesToCheck = [
    deviceSettingTypes.AUTO_START_MODE,
    deviceSettingTypes.CLEANING_MODE,
    deviceSettingTypes.CONNECT_CARE,
    deviceSettingTypes.GESTURE_MODE,
    deviceSettingTypes.VIBRATION_MODE,
    deviceSettingTypes.LOCATE_MY_DEVICE,
    deviceSettingTypes.LED_SETTING,
    deviceSettingTypes.EXTENDED_LED_SETTING,
    deviceSettingTypes.RESPONSIVE_DRAW,
    deviceSettingTypes.VAPE_CLOUD_SIZE,
    deviceSettingTypes.VAPE_SESSION,
    deviceSettingTypes.RESTART,
    deviceSettingTypes.FWU_UPDATE_CHECK,
    deviceSettingTypes.FLEX_PUFF,
    deviceSettingTypes.AEM_FLEX_BATTERY,
    deviceSettingTypes.AEM_PAUSE_MODE,
];
const deviceLockedSettingTypesToCheck = [
    deviceSettingTypes.AUTO_START_MODE,
    deviceSettingTypes.CLEANING_MODE,
    deviceSettingTypes.GESTURE_MODE,
    deviceSettingTypes.VIBRATION_MODE,
    deviceSettingTypes.LED_SETTING,
    deviceSettingTypes.EXTENDED_LED_SETTING,
    deviceSettingTypes.RESPONSIVE_DRAW,
    deviceSettingTypes.VAPE_CLOUD_SIZE,
    deviceSettingTypes.VAPE_SESSION,
    deviceSettingTypes.FLEX_PUFF,
    deviceSettingTypes.AEM_FLEX_BATTERY,
    deviceSettingTypes.AEM_PAUSE_MODE,
];
const holderConnectedSettingTypesToCheck = [
    deviceSettingTypes.AUTO_START_MODE,
    deviceSettingTypes.CLEANING_MODE,
    deviceSettingTypes.GESTURE_MODE,
    deviceSettingTypes.VIBRATION_MODE,
    deviceSettingTypes.LOCATE_MY_DEVICE,
    deviceSettingTypes.FLEX_PUFF,
    deviceSettingTypes.AEM_FLEX_BATTERY,
    deviceSettingTypes.AEM_PAUSE_MODE,
];
const regSettingTypesToCheck = [
    deviceSettingTypes.CONNECT_CARE,
    deviceSettingTypes.DEVICE_ADVANCED_DIAGNOSTIC,
    deviceSettingTypes.DEVICE_RENAME,
];
const deviceSettingsEnableCheckFunc = {
    [deviceSettingTypes.AEM_FLEX_BATTERY]: iotDeviceFeatureConfigService.isAemFlexBatteryEnabled,
    [deviceSettingTypes.AEM_PAUSE_MODE]: iotDeviceFeatureConfigService.isAemPauseModeEnabled,
    [deviceSettingTypes.FLEX_PUFF]: iotDeviceFeatureConfigService.isFlexPuffEnabled,
    [deviceSettingTypes.AUTO_START_MODE]: iotDeviceFeatureConfigService.isAutoStartEnabled,
    [deviceSettingTypes.CLEANING_MODE]: iotDeviceFeatureConfigService.isCleaningEnabled,
    [deviceSettingTypes.CONNECT_CARE]: iotDeviceFeatureConfigService.isConnectCareModeEnabled,
    [deviceSettingTypes.DEVICE_ADVANCED_DIAGNOSTIC]: iotDeviceFeatureConfigService.isAdvancedDiagnostic,
    [deviceSettingTypes.EXTENDED_LED_SETTING]: iotDeviceFeatureConfigService.isExtendedLedModeEnabled,
    [deviceSettingTypes.GESTURE_MODE]: iotDeviceFeatureConfigService.isGesturesStatus,
    [deviceSettingTypes.LED_SETTING]: iotDeviceFeatureConfigService.isLedModeEnabled,
    [deviceSettingTypes.LOCATE_MY_DEVICE]: iotDeviceFeatureConfigService.isDeviceAlarmEnabled,
    [deviceSettingTypes.RESPONSIVE_DRAW]: iotDeviceFeatureConfigService.isResponsiveDrawModeEnabled,
    [deviceSettingTypes.RESTART]: iotDeviceFeatureConfigService.isDeviceResetEnabled,
    [deviceSettingTypes.VAPE_CLOUD_SIZE]: iotDeviceFeatureConfigService.isVapeCloudModeEnabled,
    [deviceSettingTypes.VAPE_SESSION]: iotDeviceFeatureConfigService.isSessionProfilingEnabled,
    [deviceSettingTypes.VIBRATION_MODE]: iotDeviceFeatureConfigService.isVibrationEnabled,
};

const useDisableFeature = (settingType) => {
    const dispatch = useDispatch();
    const iotDevice = useSelector(makeSelectIotDeviceMergedWithConsumerProduct());
    const isDeviceReady = useSelector(selectIsDeviceReady);
    const isScpCheckInProgress = useSelector(selectIsScpCheckInProgress);
    const isMonoDevice = useSelector(selectIsMonoDevice);
    const {device, holder} = iotDevice;
    const {isHolderConnected: holderConnected, parentProtectionMode, status, type} = device || {};
    const {type: holderType, status: holderStatus} = holder || {};
    const isHolderInSBLMode = iotDeviceSettingsService.isHolderInSBLMode(iotDevice);
    const isHolderConnected = isMonoDevice ? true : holderConnected;
    const isDeviceLocked = parentalProtectionService.getIsLocked(parentProtectionMode);
    const isDeviceRegistered = isScpCheckInProgress ? true : productService.getIsRegistered(status);
    const isHolderRegistered = holderConnected ? productService.getIsRegistered(holderStatus) : true;
    const isRegistered = isDeviceRegistered && isHolderRegistered;
    const isDeviceTroubleshootingEnabled = marketConfigService.isTNCEnabled()
        ? tncService.isAggregatedSettingEnabled(ENABLE_ERRORS_DISPLAY)
        : true;
    const isDeviceReadyCheck = deviceReadySettingTypesToCheck.includes(settingType);
    const isHolderConnectedCheck = holderConnectedSettingTypesToCheck.includes(settingType);
    const isDeviceLockedCheck = deviceLockedSettingTypesToCheck.includes(settingType);
    const isRegCheck = regSettingTypesToCheck.includes(settingType);
    const featureEnableCheckFunc = deviceSettingsEnableCheckFunc[settingType];
    const isFeatureEnableCheck = featureEnableCheckFunc !== undefined;
    const isFeatureEnabled = isFeatureEnableCheck ? featureEnableCheckFunc() : true;
    const getFeatureAvailableType = useIsGetFeatureAvailable(settingType);

    let isDisabled;

    switch (settingType) {
        case deviceSettingTypes.DEVICE_RENAME:
            isDisabled = !isDeviceRegistered;
            break;
        case deviceSettingTypes.CONNECT_CARE:
            isDisabled = !isDeviceTroubleshootingEnabled;
            break;
        default:
            isDisabled = false;
    }

    if (isDeviceReadyCheck && !isDeviceReady) {
        isDisabled = true;
    } else if (isHolderConnectedCheck && !isHolderConnected) {
        isDisabled = true;
    } else if (isDeviceLockedCheck && isDeviceLocked) {
        isDisabled = true;
    } else if (isRegCheck && !isRegistered) {
        isDisabled = true;
    } else if (!isDisabled) {
        isDisabled = !isFeatureEnabled;
    }

    const onDisableClick = () => {
        if (settingType === deviceSettingTypes.CONNECT_CARE && !isDeviceTroubleshootingEnabled) {
            appErrorService.showConnectCareNotAvailableNotification();
        } else if (isRegCheck && (!isDeviceRegistered || !isHolderRegistered)) {
            appNotificationsService.showPromotedRegistrationNotification();
        } else if (isDeviceReadyCheck && !isDeviceReady) {
            appErrorService.showDeviceDisconnectNotification();
        } else if (isHolderConnectedCheck && !isHolderConnected) {
            if (settingType === deviceSettingTypes.CLEANING_MODE) {
                appErrorService.showCleaningHolderDisconnectNotification();
            } else {
                appErrorService.showDeviceHolderDisconnectNotification();
            }
        } else if (isDeviceLockedCheck && isDeviceLocked) {
            dispatch(showDeviceLockedModalAction());
        } else if (isHolderConnectedCheck && isHolderInSBLMode) {
            appErrorService.showUpdateIsNeededNotification(true);
        } else if (isFeatureEnableCheck && !isFeatureEnabled) {
            if (getFeatureAvailableType) {
                appRouterService.forwardToGetFeatureSectionPage(getFeatureAvailableType);
            } else {
                const isHolderCheck = iotDeviceConfig.isHolderFeatureRelated(type, holderType, settingType);

                appErrorService.showUpdateIsNeededNotification(isHolderCheck);
            }
        }
    };

    return {disabled: isDisabled, onDisableClick};
};

export default useDisableFeature;
